import { useDisclosure } from '@sequencehq/utils'
import {
  Text,
  GreyGrey0,
  GreyGrey10,
  GreyGrey100,
  GreyGrey20,
  GreyGrey60,
  GreyGrey80,
  GreyWhite,
  IconTrendingDown,
  IconTrendingUp,
  IndigoIndigo50,
  IndigoIndigo60,
  Link,
  Popover,
  Portal,
  Primary12UppercaseSemiBold,
  Primary13Regular,
  Primary14SemiBold,
  Primary20SemiBold,
  standardBorder,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { InvoicesPopover } from 'components/Invoices/InvoicesGrid/InvoicesPopover'
import {
  TotalChangeDirection,
  useInvoicesGrid
} from 'components/Invoices/InvoicesGrid/useInvoicesGrid'
import Spinner from 'components/Loading/Spinner'
import { useParams } from 'react-router-dom'
import { FC, useLayoutEffect, useRef } from 'react'
import { InvoicesGridToolbarV3 } from 'components/Invoices/InvoicesGrid/InvoicesGridToolbarV3'
import { match } from 'ts-pattern'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type CustomerInvoices = {
  id: string
  invoices: Record<string, Dashboardv99990101Api.Invoices.Invoice[]>
}

export type InvoicesView = {
  model: CustomerInvoices
}

/**
 * CSS classes? What are they?
 *
 * But, ya, creating bonus components for just housing
 * styles adds quite the code overhead, and I'd rather avoid
 * having all of the styling properties in the main JSX code, so I'm just
 * taking a CSS-in-JS friendly approach to classes here.
 */

const styles = {
  row: {
    height: '40px',
    borderBottom: standardBorder,
    alignItems: 'center'
  },
  cell: {
    height: '100%',
    alignItems: 'center',
    padding: '0 12px',
    textOverflow: 'ellipsis',
    justifyContent: 'space-between',
    ...Primary13Regular
  },
  invoiceBadge: {
    padding: '0 4px',
    backgroundColor: GreyGrey20,
    ...Primary12UppercaseSemiBold,
    color: GreyGrey60,
    borderRadius: '3px'
  },
  header: {
    color: GreyGrey100,
    padding: '16px 24px',
    borderBottom: standardBorder,
    height: '64px',
    alignItems: 'center',
    ...Primary20SemiBold
  },
  tools: {
    color: GreyGrey100,
    padding: '16px 24px',
    borderBottom: standardBorder,
    height: '65px',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  currencySelect: {
    width: '180px'
  }
}

const CELL_WIDTH = '184px'

export const InvoicesGrid = () => {
  const { loading, data } = useInvoicesGrid()
  const contentRef = useRef<HTMLDivElement>(null)
  const { invoiceId } = useParams()

  useLayoutEffect(() => {
    if (!loading && data && contentRef.current) {
      contentRef.current.scrollLeft =
        contentRef.current.scrollWidth - contentRef.current.clientWidth
    }
  }, [data, loading])

  if (!data) {
    return (
      <Flex flexDirection="column" width="100%" height="100%">
        <Spinner />
      </Flex>
    )
  }

  return (
    <Grid gridTemplateRows="auto 1fr" width="100%" height="100%" minHeight={0}>
      <InvoicesGridToolbarV3 data={data} />

      {loading ? (
        <Flex
          flexDirection="column"
          width="100%"
          height="100%"
          alignItems="center"
          justifyContent="center"
          minHeight={0}
        >
          <Spinner />
        </Flex>
      ) : (
        <Grid
          ref={contentRef}
          data-testid="invoices.grid"
          className="Parent"
          position="relative"
          zIndex={1}
          width="100%"
          minHeight={0}
          templateAreas={`
        "left-header table-header right-header"
        "sticky-left table sticky-right"
        "left-footer table-footer right-footer"`}
          templateRows="auto 1fr auto"
          templateColumns="195px 1fr minmax(195px, 80%)"
          overflow="auto"
        >
          {/**
           * HEADER
           * ===============================================================
           *
           * The Customer name header - sticky and fixed at the top left corner
           */}
          <GridItem
            position="sticky"
            top="0"
            left="0"
            gridArea="left-header"
            backgroundColor={GreyWhite}
            borderBottom={standardBorder}
            borderRight={standardBorder}
            zIndex="6"
          >
            <Flex
              {...styles.cell}
              {...Primary12UppercaseSemiBold}
              color={GreyGrey60}
              textTransform="uppercase"
              paddingLeft="24px"
            >
              Customer
            </Flex>
          </GridItem>

          {/**
           * The month column labels - this are sticky on the vertical axis,
           * but scroll horizontally. This keep the headers in line with the
           * main table content when scrolling horizontally.
           */}
          <GridItem
            position="sticky"
            top="0"
            gridArea="table-header"
            backgroundColor={GreyWhite}
            borderBottom={standardBorder}
            zIndex="5"
          >
            <Flex {...styles.row} borderBottom="none">
              {data.months.map(month => (
                <Flex
                  key={month.value}
                  width={CELL_WIDTH}
                  {...styles.cell}
                  {...Primary12UppercaseSemiBold}
                  color={GreyGrey60}
                  textTransform="uppercase"
                  _notLast={{
                    borderRight: standardBorder
                  }}
                >
                  {month.label}
                </Flex>
              ))}
            </Flex>
          </GridItem>

          {/**
           * The total column label - fixed at the top right
           */}

          <GridItem
            position="sticky"
            top="0"
            right="0"
            gridArea="right-header"
            backgroundColor={GreyGrey0}
            borderBottom={standardBorder}
            borderLeft={standardBorder}
            zIndex="6"
          >
            <Flex
              {...styles.cell}
              {...Primary12UppercaseSemiBold}
              color={GreyGrey80}
              textTransform="uppercase"
            >
              Total
            </Flex>
          </GridItem>

          {/**
           * MAIN CONTENT
           * ===============================================================
           *
           * Customer names - this area is sticky on the horizontal axis only,
           * and otherwise scrolls vertically
           */}
          <GridItem
            gridArea="sticky-left"
            position="sticky"
            left="0"
            zIndex="5"
            backgroundColor={GreyWhite}
            boxShadow="0px 16px 24px 0px rgba(15, 23, 42, 0.08)"
            borderRight={standardBorder}
          >
            {data.customers.map(customer => (
              <Flex
                {...styles.row}
                key={customer.id}
                _last={{
                  borderBottom: 'none'
                }}
              >
                <Text
                  {...styles.cell}
                  color={IndigoIndigo60}
                  paddingLeft="24px"
                  height="min-content"
                  isTruncated
                >
                  <Link.Unstyled
                    target="_blank"
                    to={`/customers/${customer.id}`}
                  >
                    {customer.name}
                  </Link.Unstyled>
                </Text>
              </Flex>
            ))}
          </GridItem>

          {/**
           * The main table itself. This is not sticky, and otherwise scrolls normally.
           * All of the surrounding grid areas _are_ sticky in various ways in order to
           * scroll correctly in relation to this central element.
           */}

          <GridItem
            gridArea="table"
            backgroundColor={GreyWhite}
            position="relative"
            zIndex="1"
          >
            {/**
             * A bit of smoke and mirrors - this provides the background to the main
             * table which gives the illusion of the columns extending down to the footer
             */}
            <Flex
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              zIndex="0"
            >
              {data.months.map(month => (
                <Flex
                  key={month.value}
                  width={CELL_WIDTH}
                  {...styles.cell}
                  _notLast={{
                    borderRight: standardBorder
                  }}
                ></Flex>
              ))}
            </Flex>
            {data.customers.map(customer => (
              <Flex
                {...styles.row}
                key={customer.id}
                _last={{
                  borderBottom: 'none'
                }}
              >
                {data.months.map(month => {
                  /** All the outer gubbins is all in support of this! The cell rendering. */
                  const invoicesForCell = data.getInvoices({
                    customerId: customer.id,
                    month: month.value
                  })

                  const total = data.getTotal({
                    customerId: customer.id,
                    month: month.value
                  })

                  const changeDirection =
                    data.getPrevMonthTotalChangeByCustomer({
                      customerId: customer.id,
                      month: month.value
                    })

                  return invoicesForCell.length > 1 ? (
                    <MultiInvoiceCell
                      key={month.value}
                      invoiceId={invoiceId}
                      invoicesForCell={invoicesForCell}
                      total={total}
                      changeDirection={changeDirection}
                    />
                  ) : (
                    <SingleInvoiceCell
                      key={month.value}
                      invoiceId={invoiceId}
                      invoicesForCell={invoicesForCell}
                      total={total}
                      changeDirection={changeDirection}
                    />
                  )
                })}
              </Flex>
            ))}
          </GridItem>

          {/**
           * Customer totals - this area is sticky on the horizontal axis only,
           * and otherwise scrolls vertically
           */}
          <GridItem
            gridArea="sticky-right"
            backgroundColor={GreyGrey0}
            position="sticky"
            right="0"
            zIndex="5"
            borderLeft={standardBorder}
          >
            {data.customers.map(customer => (
              <Flex
                {...styles.row}
                key={customer.id}
                _last={{
                  borderBottom: 'none'
                }}
              >
                <Flex {...styles.cell} {...Primary14SemiBold}>
                  {data.getTotal({ customerId: customer.id })}
                </Flex>
              </Flex>
            ))}
          </GridItem>

          {/**
           * FOOTER
           * ==============================================================
           *
           * The left of the footer is sticky vertically and horizontally -
           * i.e. it is always chilling down at the bottom left.
           */}
          <GridItem
            position="sticky"
            bottom="0"
            left="0"
            gridArea="left-footer"
            backgroundColor={GreyWhite}
            borderTop={standardBorder}
            borderRight={standardBorder}
            zIndex="10"
          >
            <Flex {...styles.cell} justifyContent="flex-end">
              Total
            </Flex>
          </GridItem>

          {/**
           * The table footer is sticky vertically, but scrolls horizontally.
           * This ensures the footer data, which are the totals for each month,
           * scrolls in line with the main table columns.
           */}
          <GridItem
            position="sticky"
            bottom="0"
            gridArea="table-footer"
            backgroundColor={GreyGrey0}
            borderTop={standardBorder}
            zIndex="2"
          >
            <Flex {...styles.row} borderBottom="none">
              {data.months.map(month => {
                const changeDirection = data.getPrevMonthTotalChangeByMonth({
                  month: month.value
                })

                return (
                  <Flex
                    key={month.value}
                    width={CELL_WIDTH}
                    {...styles.cell}
                    {...Primary14SemiBold}
                    color={getChangeDirectionColor(changeDirection)}
                    _notLast={{
                      borderRight: standardBorder
                    }}
                    justifyContent="flex-start"
                    gap="8px"
                  >
                    <ChangeDirectionIcon direction={changeDirection} />
                    {data.getTotal({ month: month.value })}
                  </Flex>
                )
              })}
            </Flex>
          </GridItem>
          {/**
           * The right of the footer is the grand total for the current
           * overview, and is fixed down at the bottom right in all scenarios
           */}
          <GridItem
            position="sticky"
            bottom="0"
            right="0"
            gridArea="right-footer"
            backgroundColor={GreyGrey0}
            borderTop={standardBorder}
            borderLeft={standardBorder}
            zIndex="6"
          >
            <Flex {...styles.cell} {...Primary14SemiBold} color={GreyGrey80}>
              {data.getTotal()}
            </Flex>
          </GridItem>
        </Grid>
      )}
    </Grid>
  )
}

type MultiInvoiceCellProps = {
  invoiceId: string | undefined
  invoicesForCell: string[]
  total: string
  changeDirection: TotalChangeDirection
}

const MultiInvoiceCell: FC<MultiInvoiceCellProps> = ({
  invoiceId,
  invoicesForCell,
  total,
  changeDirection
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure()

  const isActive = invoiceId && invoicesForCell.includes(invoiceId)

  return (
    <Popover.Root
      trigger="click"
      placement="bottom-start"
      onOpen={onOpen}
      onClose={onClose}
      isOpen={isOpen}
      isLazy
    >
      <Popover.Trigger>
        <Flex
          width={CELL_WIDTH}
          {...styles.cell}
          borderLeft={`3px solid ${isActive ? IndigoIndigo50 : 'transparent'}`}
          backgroundColor={isActive ? GreyGrey10 : GreyWhite}
          _notLast={{
            borderRight: standardBorder
          }}
          _hover={{
            backgroundColor: GreyGrey10
          }}
        >
          <Flex
            zIndex="10"
            cursor="pointer"
            width="100%"
            gap="8px"
            justifyContent="flex-end"
          >
            {invoicesForCell.length > 1 ? (
              <Box {...styles.invoiceBadge}>{invoicesForCell.length}</Box>
            ) : null}
            <ChangeDirectionIcon direction={changeDirection} />
            <Text color={getChangeDirectionColor(changeDirection)}>
              {total}
            </Text>
          </Flex>
          <Portal>
            <Popover.Content width="280px">
              <Popover.Body p="0">
                <InvoicesPopover
                  invoiceIds={invoicesForCell}
                  total={total}
                ></InvoicesPopover>
              </Popover.Body>
            </Popover.Content>
          </Portal>
        </Flex>
      </Popover.Trigger>
    </Popover.Root>
  )
}

type SingleInvoiceCellProps = {
  invoiceId: string | undefined
  invoicesForCell: string[]
  total: string
  changeDirection: TotalChangeDirection
}

const SingleInvoiceCell: FC<SingleInvoiceCellProps> = ({
  invoiceId,
  invoicesForCell,
  total,
  changeDirection
}) => {
  return (
    <Flex
      as={Link.Unstyled}
      to={`/invoices/${invoicesForCell[0]}`}
      width={CELL_WIDTH}
      {...styles.cell}
      borderLeft={`3px solid ${
        invoiceId && invoicesForCell.includes(invoiceId)
          ? IndigoIndigo50
          : 'transparent'
      }`}
      _notLast={{
        borderRight: standardBorder
      }}
      _hover={{
        backgroundColor: GreyGrey10
      }}
    >
      <Flex
        zIndex="10"
        cursor="pointer"
        width="100%"
        gap="8px"
        justifyContent="flex-end"
      >
        <ChangeDirectionIcon direction={changeDirection} />
        <Text color={getChangeDirectionColor(changeDirection)}>{total}</Text>
      </Flex>
    </Flex>
  )
}

const getChangeDirectionColor = (changeType: TotalChangeDirection): string => {
  return match(changeType)
    .with('INCREASE', () => 'green')
    .with('DECREASE', () => 'red')
    .with(undefined, () => GreyGrey100)
    .exhaustive()
}

const ChangeDirectionIcon: FC<{ direction: TotalChangeDirection }> = ({
  direction
}) => {
  if (!direction) return null

  const iconProps = {
    width: '16px',
    height: '16px',
    color: getChangeDirectionColor(direction)
  }

  return (
    <Box flexShrink="0">
      {match(direction)
        .with('INCREASE', () => <IconTrendingUp {...iconProps} />)
        .with('DECREASE', () => <IconTrendingDown {...iconProps} />)
        .exhaustive()}
    </Box>
  )
}
